import { Trans, useLingui } from "@lingui/react/macro";
import { ImageIcon } from "lucide-react";
import { useId, useState } from "react";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { type components, useApiErrorMessage } from "@/shared/api";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useUpdateCover } from "../../api/useUpdateCover";
import { coverHeroId } from "../../model/coverHeroId";
import { heroTint } from "../../model/heroTint";
import type { PlayerSummary } from "../../model/PlayerSummary";
import { CoverPicker } from "./CoverPicker";
import { ProfileStats } from "./ProfileStats";
import { RankCard } from "./RankCard";

// Сколько самых частых героев под ником.
const TOP_HEROES = 3;

type ProfileHeaderProps = {
  user: components["schemas"]["MeOut"];
  // Сводки нет или она ещё считается — без героев, ранга и итогов.
  summary: PlayerSummary | null;
  pending: boolean;
  analyzedMatches: number | undefined;
};

// Шапка профиля по макету design/prototype/ProfileRelease.dc.html: обложка — арт и цвет
// героя (выбранного или самого частого), аватар Steam в кольце цвета героя, ник, с каких пор
// в Deadlock и в Relock, самые частые герои, ранг и итоги за всё время. Кнопка «Обложка»
// открывает выбор под шапкой.
export const ProfileHeader = ({ user, summary, pending, analyzedMatches }: ProfileHeaderProps) => {
  const { i18n, t } = useLingui();
  const lookup = useHeroLookup();
  const updateCover = useUpdateCover();
  const errorMessage = useApiErrorMessage(updateCover.error);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerId = useId();

  const heroes = summary?.heroes ?? [];
  // Пока сохраняется — уже новая обложка.
  const cover = (updateCover.isPending ? updateCover.variables.body.cover : null) ?? user.cover;
  const heroId = coverHeroId(cover, heroes);
  const hero = heroId === null ? null : lookup(heroId);
  const coverName =
    cover.kind === "hero" ? (hero?.name ?? "") : cover.kind === "auto" ? t`auto` : t`none`;
  const name = user.display_name ?? user.steam_id ?? t`Player`;

  const day = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const joined = day.format(new Date(user.joined_at));
  const firstMatch =
    summary?.first_match_at == null ? null : day.format(new Date(summary.first_match_at));

  return (
    <section
      aria-label={t`Player`}
      className="flex flex-col overflow-hidden rounded-[14px] border border-border bg-surface"
    >
      <div
        data-cover={cover.kind}
        className="relative h-[168px] bg-surface-raised"
        style={{ backgroundColor: heroTint(hero?.color ?? null) }}
      >
        {hero?.backgroundUrl != null && (
          <img
            src={hero.backgroundUrl}
            alt=""
            decoding="async"
            className="absolute inset-0 size-full object-cover object-[right_30%]"
          />
        )}
        <Button
          variant="outline"
          aria-expanded={pickerOpen}
          aria-controls={pickerId}
          onClick={() => {
            setPickerOpen(!pickerOpen);
          }}
          className="absolute top-3.5 right-3.5 h-10 bg-background/80 font-semibold"
        >
          <ImageIcon aria-hidden="true" />
          <Trans>Cover: {coverName}</Trans>
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-x-6 gap-y-4 px-7 pb-5.5">
        <span
          className="relative -mt-16 size-32 shrink-0 rounded-full bg-border p-[5px] shadow-[0_0_0_5px_var(--color-surface)]"
          style={{ backgroundColor: hero?.color ?? undefined }}
        >
          {user.avatar_url === null ? (
            <span
              aria-hidden="true"
              className="flex size-full items-center justify-center rounded-full bg-surface-raised font-display text-5xl text-muted"
            >
              {name.slice(0, 1).toLocaleUpperCase()}
            </span>
          ) : (
            <img
              src={user.avatar_url}
              alt=""
              className="size-full rounded-full bg-surface-raised object-cover"
            />
          )}
        </span>

        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-1 pt-3.5">
          <h1 className="font-display text-[42px] leading-[1.05] [overflow-wrap:anywhere]">
            {name}
          </h1>
          <p className="text-sm text-muted">
            {firstMatch === null ? (
              <Trans>In Relock since {joined}</Trans>
            ) : (
              <Trans>
                In Deadlock since {firstMatch} · in Relock since {joined}
              </Trans>
            )}
          </p>
          {heroes.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <span className="text-[13px] text-subtle">
                <Trans>Most played</Trans>
              </span>
              {heroes.slice(0, TOP_HEROES).map((stat) => {
                const top = lookup(stat.hero_id);
                return (
                  <span
                    key={stat.hero_id}
                    className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-border bg-surface-raised py-0.5 pr-2.5 pl-[3px] text-[13px]"
                  >
                    <HeroAvatar
                      name={top.name}
                      iconUrl={top.iconUrl}
                      size={24}
                      className="rounded-full"
                    />
                    <span>{top.name}</span>
                    <span className="font-mono text-subtle">{stat.matches}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {summary?.rank_badge != null && (
          <RankCard badge={summary.rank_badge} weekAgo={summary.rank_badge_week_ago} />
        )}
      </div>

      {pending ? (
        <Skeleton className="h-[86px] rounded-none" />
      ) : (
        summary !== null &&
        summary.matches > 0 && <ProfileStats summary={summary} analyzedMatches={analyzedMatches} />
      )}

      {pickerOpen && (
        <CoverPicker
          id={pickerId}
          cover={cover}
          heroes={heroes}
          disabled={updateCover.isPending}
          onPick={(next) => {
            updateCover.mutate({ body: { cover: next } });
          }}
        />
      )}
      {errorMessage !== null && (
        <p role="alert" className="px-7 pb-4 text-sm text-bad">
          {errorMessage}
        </p>
      )}
    </section>
  );
};
